import React, { useContext, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import AuthContext from "../context/AuthContext";
import {
  Checkbox,
  FormControlLabel,
  List,
  Modal,
  Stack,
  styled,
} from "@mui/material";
import {
  DateField,
  DatePicker,
  DatePickerToolbar,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Info, UserType, WorkPlanContainer } from "../utils/types";
import { SubPageTitleWithUnderline } from "../components/common/SubPageTitleWithUnderline";
import { PageWrapper } from "../components/common/PageWrapper";
import { KachelBox } from "../components/common/KachelBox";
import { Monteur } from "../components/arbeitsplan/Monteur";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import BadgeIcon from "@mui/icons-material/Badge";

export const AccountCircle = styled(AccountCircleIcon)`
  margin-right: 5px;
`;

export const MNIcon = styled(BadgeIcon)`
  margin-right: 5px;
`;

const theme = createTheme();

export default function BenutzerverwaltungPage() {
  let { registerUser } = useContext(AuthContext);
  let { user } = useContext(AuthContext);
  const currentUser: UserType = user;
  const [selectedUser, setSelectedUser] = React.useState<UserType | null>(null);
  const [newPassword, setNewPassword] = React.useState("");
  const [userList, setUserList] = useState<UserType[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  if (
    !currentUser ||
    (currentUser.bemerkung != "GF" && currentUser.bemerkung != "Personal")
  ) {
    return <></>; // TODO: CustomPage: "Keine Berechtigung"?
  }

  useEffect(() => {
    const getAllUsers = async () => {
      const response = await fetch(`/api/getAllUsers/`);
      const data: UserType[] = await response.json();
      const sortedData = data.sort((a, b) =>
        a.last_name.localeCompare(b.last_name)
      );
      setUserList(sortedData);
    };
    getAllUsers();
  }, []);

  const handlePasswordReset = (user: UserType) => {
    setIsModalOpen(true);
    setSelectedUser(user);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value);
  };

  const handlePasswordSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (selectedUser) {
      // Send request to api/change-password with currentPassword and newPassword
      fetch(`/api/reset-password/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: selectedUser.id,
          newPassword: newPassword,
        }),
      });

      setSelectedUser(null);
      setNewPassword("");
    }
  };

  const handleDeactivateUser = async (user: UserType) => {
    const isConfirmed = window.confirm(
      `Sind Sie sicher, dass Sie den Benutzer ${user.username} deaktivieren möchten?`
    );

    if (!isConfirmed) {
      return;
    }

    const response = await fetch(`/api/deactivate-user/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId: user.id,
      }),
    });

    const newPassword = Math.random().toString(36).slice(-8);
    fetch(`/api/reset-password/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId: user.id,
        newPassword: newPassword,
      }),
    });

    if (response.ok) {
      setUserList((prevUserList) =>
        prevUserList.filter((u) => u.id !== user.id)
      );
      alert(`Benutzer ${user.username} wurde erfolgreich deaktiviert.`);
    } else {
      console.error("Failed to deactivate user");
    }
  };

  // TODOS: Angleichen mit den anderen Kacheln. So sieht es nicht so einheitlich aus
  return (
    <PageWrapper>
      <SubPageTitleWithUnderline title="Benutzerverwaltung" />
      <KachelBox>
        {userList.map((user) => (
          <Grid item xs={12} md={6} key={user.id}>
            <WorkPlanContainer
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Info>
                <Monteur userList={[user]} />
                <div>
                  <AccountCircle />
                  {user.username}
                </div>
                <div>
                  <MNIcon />
                  {user.mitarbeiternummer}
                </div>
              </Info>
              <Button
                variant="contained"
                onClick={() => handlePasswordReset(user)}
                style={{ height: "50px", width: "120px" }}
                sx={{ mt: 3, mb: 2 }}
              >
                Passwort zurücksetzen
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => handleDeactivateUser(user)}
                style={{ height: "50px", width: "120px" }}
                sx={{ mt: 3, mb: 2 }}
              >
                Deaktivieren
              </Button>
            </WorkPlanContainer>
          </Grid>
        ))}
      </KachelBox>

      {selectedUser && (
        <Modal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <div
            className="thread-list-item"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "10px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              backgroundColor: "#fff",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              padding: "20px",
            }}
          >
            <Box
              component="form"
              noValidate
              onSubmit={handlePasswordSubmit}
              sx={{ mt: 3 }}
            >
              <Typography component="h2" variant="h6">
                Passwort zurücksetzen für {selectedUser.username}
              </Typography>
              <TextField
                required
                fullWidth
                id="newPassword"
                label="Neues Passwort"
                name="newPassword"
                type="password"
                value={newPassword}
                onChange={handlePasswordChange}
                sx={{ mt: 2 }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                Passwort zurücksetzen
              </Button>
            </Box>
          </div>
        </Modal>
      )}
    </PageWrapper>
  );
}
